import React, { useState, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import ImageUploader from '../ImageUploader';
import IngredientsSelector from '../IngredientsSelector';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { fileToBase64 } from '../../services/geminiService';

interface SketchToolProps {
    onGenerate: (cost: number, quality: 'standard' | 'hd', promptBuilder: (onStatusUpdate?: (msg: string) => void) => Promise<{ prompt: string, images?: any[] }>) => Promise<void>;
    isProcessing: boolean;
}

const SketchTool: React.FC<SketchToolProps> = ({ onGenerate, isProcessing }) => {
    const { t } = useLanguage();
    const uploaderRef = useRef<{ clearFiles: () => void }>(null);
    
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [selectedMetal, setSelectedMetal] = useState('Gold');
    const [selectedFinish, setSelectedFinish] = useState('Polished');
    const [selectedProcess, setSelectedProcess] = useState('None');
    const [selectedStyle, setSelectedStyle] = useState('None');
    const [selectedGemstones, setSelectedGemstones] = useState<string[]>(['None']);

    const handleGenerate = async (quality: 'standard' | 'hd') => {
        if (uploadedFiles.length === 0) return;

        // Cost Adjustment for 75% Profit Margin:
        // Standard (Flash): 20 Swarnas (slightly higher for Sketch interp)
        // HD (Pro): 60 Swarnas
        const cost = quality === 'standard' ? 20 : 60;

        await onGenerate(cost, quality, async (onStatusUpdate) => {
            try {
                if (onStatusUpdate) onStatusUpdate("Analyzing sketch lines...");

                const file = uploadedFiles[0];
                const base64Data = await fileToBase64(file);
                const imagePart = { inlineData: { mimeType: file.type, data: base64Data } };

                // Logic Fix: Filter out 'None' to handle mixed selections correctly
                const activeGemstones = selectedGemstones.filter(g => g !== 'None');
                const gemstones = activeGemstones.length === 0 
                    ? 'No gemstones' 
                    : `Gemstone setting: ${activeGemstones.join(', ')}`;

                const styleContext = selectedStyle !== 'None' ? `Artistic Style: ${selectedStyle}` : '';

                // Define Process Aesthetics - Critical for Interpretation
                let processDescription = "";
                switch (selectedProcess) {
                    case 'Handmade':
                        processDescription = "Manufacturing Style: Handmade, interpret lines as organic hammered metal, soft manual curves.";
                        break;
                    case 'CNC/Laser':
                        processDescription = "Manufacturing Style: Laser Cut/CNC, interpret lines as sharp machine cuts, precision geometry.";
                        break;
                    case 'Navishi':
                        processDescription = "Manufacturing Style: Navishi (Nakshi) Work, interpret lines as deep relief hand-engraving.";
                        break;
                    case 'CAD/Casting':
                        processDescription = "Manufacturing Style: CAD Designed & Cast, interpret lines as smooth fillets and baked curves.";
                        break;
                    default:
                        processDescription = "";
                }

                const prompt = `
                Task: Convert this sketch into a photorealistic jewelry product image.
                
                Interpretation:
                - Treat drawn lines as solid ${selectedMetal} metalwork.
                - Apply a ${selectedFinish} finish to the metal surfaces.
                - ${styleContext}
                - ${processDescription}
                - ${gemstones}
                
                Output Style:
                - Photorealistic 3D Render
                - Studio Lighting
                - High Value Jewelry Photography
                `;

                return { prompt, images: [imagePart] };

            } catch (error) {
                console.error("Error processing sketch:", error);
                throw error;
            }
        });
    };

    return (
        <div className="space-y-8">
            <div className="space-y-3">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">Sketch Upload</label>
                <ImageUploader ref={uploaderRef} onFilesChange={setUploadedFiles} disabled={isProcessing} multiple={false} />
            </div>
            
            <IngredientsSelector 
                selectedMetal={selectedMetal} setSelectedMetal={setSelectedMetal}
                selectedFinish={selectedFinish} setSelectedFinish={setSelectedFinish}
                selectedProcess={selectedProcess} setSelectedProcess={setSelectedProcess}
                selectedStyle={selectedStyle} setSelectedStyle={setSelectedStyle}
                selectedGemstones={selectedGemstones} setSelectedGemstones={setSelectedGemstones}
            />
            
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-black/5 dark:border-white/5">
                <button 
                    onClick={() => handleGenerate('standard')} 
                    disabled={isProcessing || uploadedFiles.length === 0} 
                    className="flex flex-col items-center justify-center gap-1 py-4 rounded-2xl font-bold transition-all active:scale-95 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 border border-transparent disabled:opacity-50 group"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                            <div className="flex items-center gap-2">
                                <Icon name="sparkles" className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-white" />
                                <span>Standard</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-40">20 Swarnas</span>
                        </>
                    )}
                </button>

                <button 
                    onClick={() => handleGenerate('hd')} 
                    disabled={isProcessing || uploadedFiles.length === 0} 
                    className="flex flex-col items-center justify-center gap-1 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-apple-gold/20 hover:shadow-apple-gold/40 hover:scale-[1.02] active:scale-95 bg-apple-gold text-black border border-transparent disabled:opacity-50"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                            <div className="flex items-center gap-2">
                                <Icon name="diamond" className="w-4 h-4 text-black" />
                                <span>HD Premium</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-60">60 Swarnas</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default SketchTool;